import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Lightweight theme provider with CSS-variable–based theming.
 *  - `dark` (default, our existing design)
 *  - `light` (new, inverted)
 *  - `system` (follows OS prefers-color-scheme)
 *
 * Applies `data-theme="dark|light"` to <html> so CSS variables in index.css resolve correctly.
 * Persists the user's choice in localStorage under key `reapercast-theme`.
 */

const ThemeCtx = createContext({ theme: 'dark', resolvedTheme: 'dark', setTheme: () => {} });

function getSystem() {
  try { return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'; }
  catch { return 'dark'; }
}

function applyTheme(resolved) {
  const html = document.documentElement;
  html.setAttribute('data-theme', resolved);
  // Keep meta theme-color in sync for PWA splash / iOS status bar
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'light' ? '#f6f5f2' : '#0a0a0c');
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => localStorage.getItem('reapercast-theme') || 'dark');
  const [resolved, setResolved] = useState(() => (theme === 'system' ? getSystem() : theme));

  useEffect(() => { applyTheme(resolved); }, [resolved]);

  useEffect(() => {
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      const onChange = () => setResolved(getSystem());
      mq.addEventListener?.('change', onChange);
      setResolved(getSystem());
      return () => mq.removeEventListener?.('change', onChange);
    }
    setResolved(theme);
  }, [theme]);

  const setTheme = useCallback((t) => {
    localStorage.setItem('reapercast-theme', t);
    setThemeState(t);
  }, []);

  return <ThemeCtx.Provider value={{ theme, resolvedTheme: resolved, setTheme }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
